import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@unified-checkout/design-system';

import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  SYSTEM_EVENTS_GROUPING_CODES,
  SYSTEM_EVENTS_SRC_FEATURES,
} from '@app/dashboard/constants/tracking_events/system';
import { addSubscription } from '@app/dashboard/services/inner_layout/risk_center/subscriptions';
import { getMerchantId } from '@app/dashboard/utils/localStorageUtils';
import { onToasterShowHandler } from '@app/dashboard/utils/tracking/onToasterShowHandler';
import { notify } from '@app/dashboard/utils/notifier';

import SubscriptionImg from '@app/dashboard/assets/images/innerLayout/subscription.png';

import styles from './Subscription.module.scss';

const Subscription = () => {
  const { t } = useTranslation();
  const merchantId = getMerchantId();

  const queryClient = useQueryClient();

  const { mutate: addSubscriptionMutate, isPending } = useMutation({
    mutationFn: (data: { merchant: number; plan: number }) =>
      addSubscription(data),
    onSuccess: () => {
      const successMessage = t(
        'innerLayout.riskCenter.subscription.successMessage'
      );

      notify.success(successMessage, {
        timeout: 2,
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'success',
            trackingCode:
              SYSTEM_EVENTS_GROUPING_CODES.RISK_CENTER.MAIN.SUBSCRIPTION,
            msg: successMessage,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.RISK_CENTER.MAIN,
          }),
      });

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.GET_SUBSCRIPTION_DATA],
        });
      }, 2500);
    },
    onError: (axiosErr: AxiosError<{ message?: string }>) => {
      let message = t('errors.somethingWentWrong');

      if (axiosErr.response?.data?.message) {
        message = axiosErr.response?.data?.message;
      } else if (axiosErr.message) {
        message = axiosErr.message;
      }

      notify.error(message, {
        timeout: 2,
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'error',
            trackingCode:
              SYSTEM_EVENTS_GROUPING_CODES.RISK_CENTER.MAIN.SUBSCRIPTION,
            msg: message,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.RISK_CENTER.MAIN,
          }),
      });
    },
  });

  const handleSubscribeClick = () => {
    addSubscriptionMutate({ merchant: merchantId, plan: 1 });
  };

  return (
    <div className={styles['subscription']}>
      <img src={SubscriptionImg} alt="Subscription" />
      <p className={styles['subscription__text']}>
        {t('innerLayout.riskCenter.subscription.subscriptionMessage')}
      </p>
      <Button
        text={t('innerLayout.riskCenter.subscription.subscribe')}
        onClickHandler={handleSubscribeClick}
        loading={isPending}
        disabled={isPending}
      />
    </div>
  );
};

export default Subscription;
